/**
 * API 통신을 위한 Axios 인스턴스 설정
 */

import axios from 'axios';
import { mergeWithNavigationSignal } from '@/utils/navigationAbort';

// API 기본 URL 설정 (환경변수 또는 기본값 사용)
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';

// ============================================
// 토큰 관리 함수
// ============================================

/**
 * AccessToken 저장
 */
export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem('accessToken', token);
  }
};

/**
 * RefreshToken 저장
 */
export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem('refreshToken', token);
  }
};

/**
 * AccessToken 가져오기
 */
export const getAccessToken = () => {
  return localStorage.getItem('accessToken');
};

/**
 * RefreshToken 가져오기
 */
export const getRefreshToken = () => {
  return localStorage.getItem('refreshToken');
};

/**
 * 토큰 삭제
 */
export const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

/**
 * 토큰 저장 (AccessToken과 RefreshToken 동시 저장)
 */
export const setTokens = (accessToken, refreshToken) => {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
};

// ============================================
// Axios 인스턴스 생성
// ============================================

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000, // 30초
  headers: {
    'Content-Type': 'application/json',
  },
});

// 토큰 갱신 중인지 추적하는 플래그
let isRefreshing = false;
// 토큰 갱신 대기 중인 요청들
let failedQueue = [];

/**
 * 대기 중인 요청들을 처리
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * RefreshToken으로 새로운 AccessToken 발급
 */
const refreshAccessToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('RefreshToken이 없습니다.');
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/user/refresh-token`,
      { refreshToken },
      { withCredentials: true }
    );

    if (response.data.success) {
      const newAccessToken = response.data.data.accessToken;
      setAccessToken(newAccessToken);
      return newAccessToken;
    } else {
      throw new Error('토큰 갱신 실패');
    }
  } catch (error) {
    clearTokens();
    throw error;
  }
};

// ============================================
// 요청 인터셉터 (AccessToken 자동 추가)
// ============================================

apiClient.interceptors.request.use(
  (config) => {
    // FormData를 전송하는 경우 Content-Type을 제거하여 axios가 자동으로 설정하도록 함
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // 라우트 전환 시 이전 화면의 미완료 요청 중단 (navigationAbort: false 로 옵트아웃)
    if (config.navigationAbort !== false) {
      config.signal = mergeWithNavigationSignal(config.signal);
    }

    // AccessToken을 Authorization 헤더에 추가
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// 응답 인터셉터 (401 에러 시 토큰 갱신)
// ============================================

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 라우트 전환 등으로 요청이 취소된 경우 — API 오류로 로깅하지 않음
    if (
      error.code === 'ERR_CANCELED' ||
      error.name === 'CanceledError' ||
      error.name === 'AbortError' ||
      (typeof axios.isCancel === 'function' && axios.isCancel(error))
    ) {
      return Promise.reject(error);
    }

    // 401 에러이고 아직 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 토큰 갱신 엔드포인트 자체에서 401이 발생한 경우는 재시도하지 않음
      if (originalRequest.url?.includes('/refresh-token')) {
        console.error('로그인 시간이 만료되었습니다. 로그아웃이 필요합니다.');
        clearTokens();
        
        // 로그아웃 처리 (완료될 때까지 대기해 UI가 즉시 반영되도록)
        const { useUserStore } = await import('@/store/user');
        const userStore = useUserStore();
        if (userStore.isLogin) {
          await userStore.logOut();
        }
        
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        // 이미 토큰 갱신 중이면 대기열에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        // RefreshToken으로 새 AccessToken 발급
        const newToken = await refreshAccessToken();
        
        // 대기 중인 요청들 처리
        processQueue(null, newToken);
        
        // 원래 요청 재시도
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // RefreshToken도 만료된 경우 로그아웃 처리
        processQueue(refreshError, null);
        
        console.error('토큰 갱신 실패, 로그아웃 처리:', refreshError);
        
        // 로그아웃 처리 (완료될 때까지 대기해 LiveDetail 등에서 관리자 메뉴가 즉시 사라지도록)
        const { useUserStore } = await import('@/store/user');
        const userStore = useUserStore();
        if (userStore.isLogin) {
          await userStore.logOut();
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    console.error('API 오류:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default apiClient;

